import React, { useEffect, useState } from 'react';
import {
  Button, FormControl, FormLabel, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter,
  ModalHeader, ModalOverlay, Select, Switch, Text, useToast,
  HStack,
} from '@chakra-ui/react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import useContacts from '../hooks/useContacts';
import { ContactDto } from '../models/contactInfo';
import apiErrorMessage from '../utils/apiError';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  workOrderId: string;
  workOrderNumber?: string;
  isSalesOrder?: boolean;
  trackingUrl?: string | null;
}

// item 1315 (Chad: "what if I did a Sales Order to price a job — can I convert it to a PO and then
// an invoice?") — the REVERSE of the PO→SO guided conversion: from a priced Sales/Work Order, cut the
// vendor PURCHASE ORDER. Pick the vendor; the SO's hardware lines land on a new PO at COST (the SO
// keeps group price — that spread is the margin), with ship-to, client PO # and tracking carried and
// the PO linked back to this order. Invoicing stays what it is today (order → invoice).
const WorkOrderCreatePoModal: React.FC<Props> = ({
  isOpen, onClose, workOrderId, workOrderNumber, isSalesOrder, trackingUrl,
}) => {
  const history = useHistory();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: contacts } = useContacts();
  const vendors = (contacts?.index ?? []).filter((c: ContactDto) => c.isVendor);
  const [vendorId, setVendorId] = useState('');
  const [hasTracking, setHasTracking] = useState(!!trackingUrl);
  const [tracking, setTracking] = useState(trackingUrl ?? '');
  const [busy, setBusy] = useState(false);
  const label = isSalesOrder ? 'Sales Order' : 'Work Order';

  useEffect(() => {
    if (isOpen) { setVendorId(''); setHasTracking(!!trackingUrl); setTracking(trackingUrl ?? ''); }
  }, [isOpen, trackingUrl]);

  const run = async () => {
    if (!vendorId) { toast({ status: 'error', title: 'Pick the vendor this PO goes to.' }); return; }
    setBusy(true);
    try {
      const { data: newPoId } = await axios.post(`${apiUrl()}WorkOrder/${workOrderId}/create-purchase-order`, {
        vendorId,
        trackingUrl: hasTracking && tracking.trim() ? tracking.trim() : undefined,
      });
      await queryClient.invalidateQueries('purchaseOrders');
      await queryClient.invalidateQueries(['workOrder', workOrderId]);
      onClose();
      toast({ status: 'success', title: 'Purchase order created', description: `Hardware from ${workOrderNumber ?? `this ${label}`} is on a new PO at cost — opening it.` });
      if (newPoId) history.push(`/PurchaseOrder/${newPoId}`);
    } catch (e: any) {
      toast({ status: 'error', title: 'Could not create the purchase order', description: apiErrorMessage(e, 'Try again.') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{`Create purchase order from ${label}`}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" mb={4} opacity={0.8}>
            {`Copies this ${label}'s hardware lines onto a new PO at your COST from the catalog; the ${label} keeps its group pricing. Ship-to, client PO # and tracking carry over, and the PO is linked back here.`}
          </Text>
          <FormControl isRequired mb={4}>
            <FormLabel fontSize="sm">Vendor</FormLabel>
            <Select placeholder="— Vendor —" value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
              {vendors.map((v: ContactDto) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </Select>
          </FormControl>
          <FormControl>
            <HStack spacing={3}>
              <Switch id="po-has-tracking" isChecked={hasTracking} onChange={(e) => { setHasTracking(e.target.checked); if (!e.target.checked) setTracking(''); }} />
              <FormLabel htmlFor="po-has-tracking" mb={0} fontSize="sm" cursor="pointer">Has tracking</FormLabel>
            </HStack>
          </FormControl>
          {hasTracking && (
            <FormControl mt={2}>
              <Input type="url" placeholder="Carrier tracking URL" value={tracking} onChange={(e) => setTracking(e.target.value)} />
            </FormControl>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button colorScheme="brandGreen" isLoading={busy} onClick={run}>Create &amp; open</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

WorkOrderCreatePoModal.defaultProps = { workOrderNumber: undefined, isSalesOrder: false, trackingUrl: undefined };

export default WorkOrderCreatePoModal;
